/**
 * 滚动帮助工具
 * 使整个应用区域（#app内，除了header和menubar）的滚动都能触发内容容器的滚动
 * 如果页面中有.partner-grid-container，则控制它的滚动而不是.page-container
 */

export const setupScrollHelper = () => {
  // 确保在DOM和样式都加载完成后执行
  if (document.readyState === 'complete') {
    // 如果页面已经加载完成，立即执行
    setTimeout(setupAppScrollHandler, 100)
  } else {
    // 如果页面正在加载，等待DOMContentLoaded事件
    window.addEventListener('DOMContentLoaded', () => {
      setTimeout(setupAppScrollHandler, 100)
    })
  }
  
  // 页面完全加载后再执行一次，确保样式和布局都已计算
  window.addEventListener('load', () => {
    setTimeout(setupAppScrollHandler, 100)
  })
}

// 设置应用级别的滚动处理器
const setupAppScrollHandler = () => {
  // 获取app元素
  const appElement = document.getElementById('app')
  if (!appElement) {
    console.warn('无法找到#app元素，滚动增强功能未启用')
    return
  }
  
  // 防止重复添加处理器
  if ((appElement as any)._scrollHandlerApplied) {
    return
  }
  
  // 标记为已处理
  (appElement as any)._scrollHandlerApplied = true
  
  // 监听整个app的wheel事件
  appElement.addEventListener('wheel', handleAppWheel, { passive: false })
  
  // 处理滚动条显示
  handleScrollbarVisibility()
  
  // 窗口大小变化时重新计算
  window.addEventListener('resize', () => {
    handleScrollbarVisibility()
  })
  
  // 监听路由变化，重新应用滚动条显示逻辑
  // 此处采用MutationObserver来监听DOM变化，因为没有直接访问Vue Router的方式
  const observer = new MutationObserver(() => {
    handleScrollbarVisibility()
  })
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  })
}

// 处理滚动条显示逻辑
const handleScrollbarVisibility = () => {
  // 查找所有.page-container元素
  const pageContainers = document.querySelectorAll('.page-container')
  
  pageContainers.forEach(container => {
    const hasPartnerGrid = document.querySelector('.partner-grid-container') !== null
    
    if (hasPartnerGrid) {
      // 如果页面中有.partner-grid-container，隐藏.page-container的滚动条
      ;(container as HTMLElement).style.overflow = 'hidden'
    } else {
      // 否则显示滚动条
      ;(container as HTMLElement).style.overflow = 'auto'
    }
  })
}

// 处理app上的wheel事件
const handleAppWheel = (event: WheelEvent) => {
  // 检查事件目标是否在header或menubar中
  if (isInExcludedArea(event.target as HTMLElement)) {
    // 如果在排除区域，不进行处理
    return
  }
  
  // 首先尝试查找.partner-grid-container
  const partnerGridContainer = document.querySelector('.partner-grid-container') as HTMLElement
  
  if (partnerGridContainer) {
    // 如果存在.partner-grid-container，控制它的滚动
    event.preventDefault()
    
    // 计算滚动量
    const deltaY = event.deltaY
    
    // 为移动设备增加平滑滚动效果
    if ('ontouchstart' in window) {
      smoothScroll(partnerGridContainer, deltaY)
    } else {
      // 桌面设备直接滚动
      partnerGridContainer.scrollTop += deltaY
    }
    
    return
  }
  
  // 如果没有.partner-grid-container，查找当前页面上的.page-container元素
  const pageContainer = findVisiblePageContainer()
  if (!pageContainer) {
    return // 没有找到可滚动的容器
  }
  
  // 阻止默认滚动行为
  event.preventDefault()
  
  // 计算滚动量
  const deltaY = event.deltaY
  
  // 为移动设备增加平滑滚动效果
  if ('ontouchstart' in window) {
    smoothScroll(pageContainer, deltaY)
  } else {
    // 桌面设备直接滚动
    pageContainer.scrollTop += deltaY
  }
}

// 查找当前页面上可见的.page-container元素
const findVisiblePageContainer = (): HTMLElement | null => {
  const containers = document.querySelectorAll('.page-container')
  if (containers.length === 0) return null
  
  // 如果只有一个容器，直接返回
  if (containers.length === 1) {
    return containers[0] as HTMLElement
  }
  
  // 如果有多个容器，找出当前可见的那个
  for (let i = 0; i < containers.length; i++) {
    const container = containers[i] as HTMLElement
    const rect = container.getBoundingClientRect()
    
    // 检查元素是否在视口内
    if (rect.top <= window.innerHeight && rect.bottom >= 0) {
      return container
    }
  }
  
  // 如果没有找到可见的容器，返回第一个
  return containers[0] as HTMLElement
}

// 判断元素是否在需要排除的区域（header、menubar等）
const isInExcludedArea = (element: HTMLElement | null): boolean => {
  if (!element) return false
  
  // 向上查找父元素，检查是否在header或菜单区域中
  let current: HTMLElement | null = element
  
  while (current) {
    // 检查元素类名
    if (
      current.classList.contains('header') || 
      current.tagName.toLowerCase() === 'header' ||
      current.classList.contains('nav-menu') ||
      current.classList.contains('el-menu') ||
      current.classList.contains('submenu') ||
      current.classList.contains('mobile-submenu') ||
      current.classList.contains('mobile-nav-menu') ||
      current.classList.contains('model-container') ||
      current.classList.contains('sidebar') // 排除侧边栏
    ) {
      return true
    }
    
    // 向上查找父元素
    current = current.parentElement
  }
  
  return false
}

// 平滑滚动函数
const smoothScroll = (element: HTMLElement, delta: number) => {
  const scrollDistance = delta
  const scrollDuration = 300 // 滚动持续时间（毫秒）
  
  let scrollStep = Math.abs(scrollDistance) / (scrollDuration / 15)
  if (scrollDistance < 0) scrollStep = -scrollStep
  
  const scrollInterval = setInterval(() => {
    if (scrollDistance > 0 && element.scrollTop >= element.scrollHeight - element.clientHeight) {
      clearInterval(scrollInterval)
      return
    }
    
    if (scrollDistance < 0 && element.scrollTop <= 0) {
      clearInterval(scrollInterval)
      return
    }
    
    element.scrollTop += scrollStep
  }, 15)
} 